import { useEffect } from "react";
import { DEFAULT_LANG, LANG_CODES, LANGS, localizePath, useI18n } from "../i18n";
import { SITE_URL, absoluteUrl, organization, website } from "./schema";

// Head management without extra dependencies. Tags already present in public/index.html
// are updated in place (never duplicated), so the prerendered HTML has exactly one of each.
function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr, key, content) {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  const el = upsert(selector, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    if (hreflang) l.setAttribute("hreflang", hreflang);
    return l;
  });
  el.setAttribute("href", href);
}

/**
 * Per-page SEO: title, description, canonical, hreflang alternates, Open Graph, robots, JSON-LD.
 * - path: route path WITHOUT the language prefix (e.g. "/katalog/lamela-a"); defaults to the current one
 * - noindex: thank-you / 404 pages
 * - jsonLd: one object or an array of schema.org objects (page specific; the Organization
 *   and WebSite blocks are added for every page)
 * - ready: set to true once the page's data has loaded; the prerender waits for it
 */
export default function Seo({ title, description, path, noindex = false, image, jsonLd, ready = true }) {
  const { lang, basePath, meta } = useI18n();
  const pagePath = path ?? basePath;
  const ld = JSON.stringify(jsonLd || null);

  useEffect(() => {
    const url = absoluteUrl(localizePath(pagePath, lang));
    document.title = title;
    document.documentElement.lang = meta.htmlLang;

    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    setLink("canonical", url);
    LANG_CODES.forEach((code) => setLink("alternate", absoluteUrl(localizePath(pagePath, code)), LANGS[code].hreflang));
    setLink("alternate", absoluteUrl(localizePath(pagePath, DEFAULT_LANG)), "x-default");

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:locale", meta.ogLocale);
    setMeta("property", "og:image", absoluteUrl(image || "/og-image.jpg"));
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((m) => m.remove());
    LANG_CODES.filter((c) => c !== lang).forEach((code) => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:locale:alternate");
      m.setAttribute("content", LANGS[code].ogLocale);
      document.head.appendChild(m);
    });

    document.head.querySelectorAll('script[data-seo="page"]').forEach((s) => s.remove());
    const blocks = [organization(), website(lang), ...[].concat(JSON.parse(ld) || [])];
    blocks.forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "page";
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
  }, [title, description, pagePath, noindex, image, ld, lang, meta]);

  // Readiness flag for tools/seo/prerender.js (and nothing else).
  useEffect(() => {
    if (ready) document.documentElement.dataset.ready = "1";
    else delete document.documentElement.dataset.ready;
  }, [ready, pagePath, lang]);

  return null;
}

export { SITE_URL };
